/** @fileoverview Calculations that computed list columns run on each row. */

import type {
  FabrikColumnComputeName,
  FabrikComputedColumn,
  FabrikComputedInput,
  FabrikGlobals,
  FabrikListColumn,
  FabrikListRefValue,
  FabrikRowValues,
} from '../../types/apps';

import type { CalendarDate } from './helpers';
import {
  calendarDateOf,
  daysBetween,
  formatCellValue,
  todayCalendarDate,
  valueForReference,
} from './helpers';

/** What one input of a calculation points at: one row field, or a list of them. */
export type ColumnComputeInputKind = 'field' | 'fields';

/** How a calculation's result is shown. */
export type ColumnComputeOutputType = 'number' | 'text';

export type ColumnComputeSpec = {
  inputs: Readonly<Record<string, ColumnComputeInputKind>>;
  outputType: ColumnComputeOutputType;
  description: string;
};

/** The calculations a computed column can run. Same table as the backend checker. */
export const COLUMN_COMPUTES: Readonly<Record<FabrikColumnComputeName, ColumnComputeSpec>> =
  Object.freeze({
    daysUntil: {
      inputs: { date: 'field' },
      outputType: 'number',
      description: 'whole days from today to the date',
    },
    daysSince: {
      inputs: { date: 'field' },
      outputType: 'number',
      description: 'whole days from the date to today',
    },
    yearsSince: {
      inputs: { date: 'field' },
      outputType: 'number',
      description: 'whole years from the date to today',
    },
    join: {
      inputs: { fields: 'fields' },
      outputType: 'text',
      description: 'the filled-in values of 2 to 5 fields joined together',
    },
  });

const DEFAULT_JOIN_SEPARATOR = ' ';

/**
 * Tells whether a list column is a computed column.
 * @param column the list column
 */
export const isComputedColumn = (column: FabrikListColumn): column is FabrikComputedColumn =>
  column.kind === 'computed';

/**
 * Counts whole years from one date to another; negative when `to` is earlier.
 * @param from start date
 * @param to end date
 */
const yearsBetween = (from: CalendarDate, to: CalendarDate): number => {
  if (daysBetween(from, to) < 0) {
    return -yearsBetween(to, from);
  }
  const beforeAnniversary = to.month < from.month || (to.month === from.month && to.day < from.day);
  return to.year - from.year - (beforeAnniversary ? 1 : 0);
};

/**
 * Tells whether a computed column input is one dataSource or globals ref.
 * @param input one entry of the column's inputs
 */
const isListRef = (input: FabrikComputedInput | undefined): input is FabrikListRefValue =>
  typeof input === 'object' &&
  input !== null &&
  !Array.isArray(input) &&
  input.type === 'ref' &&
  (input.scope === 'dataSource' || input.scope === 'globals') &&
  typeof input.field === 'string';

/**
 * Reads the value named by a single-ref input.
 * @param inputs the column's inputs
 * @param inputName the input to read
 * @param row the composed row values, keyed by datasource id
 * @param globals the globals values of this render
 */
const fieldInputValue = (
  inputs: FabrikComputedColumn['inputs'],
  inputName: string,
  row: FabrikRowValues,
  globals: FabrikGlobals
): unknown => {
  const input = inputs[inputName];
  return isListRef(input) ? valueForReference(row, input, globals) : undefined;
};

/**
 * Joins the filled-in values of the `fields` input, or returns undefined when none are filled in.
 * @param inputs the column's inputs
 * @param row the composed row values, keyed by datasource id
 * @param globals the globals values of this render
 */
const joinFields = (
  inputs: FabrikComputedColumn['inputs'],
  row: FabrikRowValues,
  globals: FabrikGlobals
): string | undefined => {
  const { fields, separator } = inputs;
  if (!Array.isArray(fields)) {
    return undefined;
  }
  const parts = fields
    .filter(isListRef)
    .map((ref) => formatCellValue(valueForReference(row, ref, globals), 'text').trim())
    .filter((part) => part !== '');
  return parts.length
    ? parts.join(typeof separator === 'string' ? separator : DEFAULT_JOIN_SEPARATOR)
    : undefined;
};

/**
 * Runs a computed column's calculation on one row.
 * Returns a number, a string, or undefined when an input is missing or is not a valid date.
 * @param column the computed column
 * @param row the composed row values, keyed by datasource id
 * @param globals the globals values of this render; `currentDate` is today
 */
export const computeColumnValue = ({
  column,
  row,
  globals,
}: {
  column: FabrikComputedColumn;
  row: FabrikRowValues;
  globals: FabrikGlobals;
}): unknown => {
  const { compute, inputs } = column;
  if (compute === 'join') {
    return joinFields(inputs, row, globals);
  }
  const date = calendarDateOf(fieldInputValue(inputs, 'date', row, globals));
  if (!date) {
    return undefined;
  }
  const today = todayCalendarDate(globals.currentDate);
  switch (compute) {
    case 'daysUntil':
      return daysBetween(today, date);
    case 'daysSince':
      return daysBetween(date, today);
    case 'yearsSince':
      return yearsBetween(date, today);
    default:
      return undefined;
  }
};
